// src/components/surveyParsers.js
// Parses the RYNO Q&A-style survey exports (Website, Paid Media, Google Ads, LSA, Meta).
// These are Google Form exports: a fixed question appears, then the client's answer
// on the following line(s). The question wording is a stable template reused for every
// client, so each type's full question/header list is hardcoded here and used as the
// "stop" marker set — this is what makes extraction reliable answer-to-answer.

function norm(s) {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function buildStopSet(arr) {
  return new Set(arr.map(norm));
}

// Finds an exact question line, then collects the following non-empty line(s) as its
// answer, stopping at the next known question/header for this survey type (or any line
// ending in '?' as a generic backstop).
function qaValue(lines, questionText, stopSet) {
  const target = norm(questionText);
  const idx = lines.findIndex(l => norm(l) === target);
  if (idx === -1) return '';
  const collected = [];
  for (let j = idx + 1; j < lines.length; j++) {
    const t = lines[j].trim();
    if (!t) { if (collected.length) break; else continue; }
    if (stopSet.has(norm(t)) || t.endsWith('?')) break;
    collected.push(t);
  }
  return collected.join(' ');
}

function extractNear(text, re) {
  const m = text.match(re);
  return m ? m[1] : '';
}

function numericBudget(raw) {
  const cleaned = (raw || '').replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

const WEBSITE_QUESTIONS = [
  'Business Info', 'What is your official, legal business name?',
  'Do you anticipate changing your name, logo, or branding before the new website launches?',
  'What is your business slogan, motto, or tagline?', 'Address', 'City', 'State', 'ZIP Code',
  'If you have additional office locations, please provide the addresses.',
  'Are the hours listed on your Google Business Profile accurate?',
  'Please provide all business license numbers.', 'What year was your business founded?',
  'Competitor 1', 'Competitor 2', 'Competitor 3',
  'What are the top 6 reasons your clients would want to choose you over your competitors?',
  'Is your current marketing agency aware of the transition to RYNO?', 'Website Details',
  'Do you currently have a website?', 'What is your current website URL?',
  'What is the URL that we will be launching the new site on?',
  "Please provide any other URLs we'll need to redirect or be aware of.",
  'Please provide all email addresses of users who will need access to the website.',
  'Do you own the written and visual content on your current website?', 'Please explain.',
  "Blog Author's Name", "Blog Author's Title", 'Brand / Design Info',
  'What do you like about your current website?', 'What do you dislike about your current website?',
  'Are there any websites that you like the look of? Specifically the homepage design.',
  "Is there anything you would like to see on the new website's homepage?",
  "Do you have any specific requests around the site's color scheme?",
  'Are there any specific colors you would like to avoid?',
  'What words describe the impression you want to convey to your site visitors?',
  'Is there anything specific you would like us to know about your brand?', 'Services',
  'If you offer any emergency or same-day services, please explain.',
  'Do you offer commercial and residential services?',
  'Which of the following service categories apply to your business? Select all that apply.',
  'Which 3-5 services are your top priority?',
  'Please explain what the average ticket cost is for each of your service categories.',
  'Please list any specific brands/products you sell, promote, or have co-ops with.',
  'Service Area Details', 'List all cities/states you provide services in.',
  'Which cities in your service area are your top priority?',
  'Are you planning to expand your offered services into new areas in the next 6-12 months?',
  'How would residents in your service area refer to the region? Examples: Upstate, Chicagoland, Tri-State Area',
  'More Info',
  'Are there any special requests, features, notes, or other details related to your new website that you would like our team to know about?',
];

const PAID_MEDIA_QUESTIONS = [
  'Ad Schedule', 'How would you prefer to schedule your Paid Media ads?',
  'Please explain the hours and days you would like your ads to run by advertising channel. Indicate if the hours should differ by service category.',
  'If your ad schedule includes hours outside your normal business hours, please confirm one of the following:',
  'Location Targeting', 'How would you like your ads to target locations?',
  'Which locations should your ads target?',
  'Would you like to target areas within a set distance from a location (radius targeting)?',
  'Are there any locations you want to exclude from targeting?', 'Budget Preference',
  'Would you like to prioritize your monthly campaign budget for certain services?',
  'What is the maximum you want to spend per lead?', 'Is there a default or recommendation?',
  'Number of Technicians on My Team:', 'Number of Appointments per technician: per week',
  'Ad Content', 'Do you have any evergreen special offers, rebates, or coupons to feature in ad content?',
  'Is there anything specific you do not want mentioned in your ad copy?',
];

const GOOGLE_ADS_QUESTIONS = [
  'Google Ads To-Do Survey', 'Do you currently have a Google Ads account?', 'CID',
  'Do you have a preferred total monthly budget for Google Ads?',
  'Do you have any additional notes or special instructions for your Google Ads campaigns?',
];

const LSA_QUESTIONS = [
  'Account Status', 'What is your current Local Services Ads (LSA) status?', 'CID',
  'Do you have a Google Ads account and LSA account that share the same CID?',
  'What would you like RYNO to do with your existing LSA account?',
  'What is the status of your Google Business Profile (GBP) as it relates to your LSA account?',
  'Please confirm the status of your business license on your LSA profile.',
  'Please confirm the status of your certificate of insurance on your LSA profile.',
  'What is the current status of background checks for your team?', 'Budget Preference',
  'Do you have a preferred total monthly budget for LSA?', 'My preferred monthly budget ($)',
  'Content', 'Do you have any additional notes or special instructions for your LSA profile?',
];

const META_QUESTIONS = [
  'Meta Ads To-Do Survey',
  'Which of these Meta (Facebook) Business features do you currently have? Select all that apply.',
  'Facebook Page Name', 'Facebook Page URL', 'What is your Meta Ad Account ID?',
  'Would you like your Instagram connected to your Meta Ads?', 'My Instagram handle:',
  'Has the Meta Pixel (website tracking code) been installed on your current website?',
  'Do you have a preferred total monthly budget for Meta Ads?',
];

export const SURVEY_TYPES = [
  { id: 'website', label: 'Website Survey', available: true },
  { id: 'paidMedia', label: 'Paid Media Survey', available: true },
  { id: 'googleAds', label: 'Google Ads Survey', available: true },
  { id: 'lsa', label: 'LSA Survey', available: true },
  { id: 'meta', label: 'Meta Survey', available: true },
  { id: 'microsoft', label: 'Microsoft Survey (template not on file yet)', available: false },
];

// Returns { fields: {candidate values to offer for applying to the client card}, raw: text }
export function parseQaSurvey(type, text) {
  const lines = text.split('\n').map(l => l.trim());
  const fields = {};

  if (type === 'website') {
    const stops = buildStopSet(WEBSITE_QUESTIONS);
    fields.legalBusinessName = qaValue(lines, 'What is your official, legal business name?', stops);
    fields.websiteUrl = qaValue(lines, 'What is your current website URL?', stops);
    const cities = qaValue(lines, 'List all cities/states you provide services in.', stops);
    const priorityCities = qaValue(lines, 'Which cities in your service area are your top priority?', stops);
    fields.serviceAreaNotes = [cities && `Service area: ${cities}`, priorityCities && `Top priority cities: ${priorityCities}`].filter(Boolean).join('\n');
    fields.valueProps = qaValue(lines, 'What are the top 6 reasons your clients would want to choose you over your competitors?', stops);
    const priorityServices = qaValue(lines, 'Which 3-5 services are your top priority?', stops);
    if (priorityServices) fields.surveyNotes = `Top priority services (from Website Survey): ${priorityServices}`;
  }

  if (type === 'paidMedia') {
    const stops = buildStopSet(PAID_MEDIA_QUESTIONS);
    fields.adScheduleNotes = qaValue(lines, 'Please explain the hours and days you would like your ads to run by advertising channel. Indicate if the hours should differ by service category.', stops);
    fields.serviceAreaNotes = qaValue(lines, 'Which locations should your ads target?', stops);
    const coupons = qaValue(lines, 'Do you have any evergreen special offers, rebates, or coupons to feature in ad content?', stops);
    fields.couponsPromos = coupons && !/^no\.?$/i.test(coupons) ? coupons : '';
    const adCopyNotes = qaValue(lines, 'Is there anything specific you do not want mentioned in your ad copy?', stops);
    if (adCopyNotes && !/^no\.?$/i.test(adCopyNotes)) fields.surveyNotes = `Ad copy notes (from Paid Media Survey): ${adCopyNotes}`;
  }

  if (type === 'googleAds') {
    const stops = buildStopSet(GOOGLE_ADS_QUESTIONS);
    const status = qaValue(lines, 'Do you currently have a Google Ads account?', stops);
    fields.accountType = /existing/i.test(status) ? 'existing' : (/don't|do not|no\b/i.test(status) ? 'new' : '');
    fields.cid = extractNear(text, /my cid is:?:?\s*([\w-]+)/i);
    const budgetRaw = qaValue(lines, 'Do you have a preferred total monthly budget for Google Ads?', stops);
    fields.budget = numericBudget(budgetRaw);
    const notes = qaValue(lines, 'Do you have any additional notes or special instructions for your Google Ads campaigns?', stops);
    if (notes && !/^n\/a$/i.test(notes)) fields.surveyNotes = `Google Ads notes: ${notes}`;
  }

  if (type === 'lsa') {
    const stops = buildStopSet(LSA_QUESTIONS);
    const status = qaValue(lines, 'What is your current Local Services Ads (LSA) status?', stops);
    fields.accountType = /existing/i.test(status) ? 'existing' : (/don't|do not|no\b/i.test(status) ? 'new' : '');
    fields.cid = extractNear(text, /my cid is:?:?\s*([\w-]+)/i);
    const budgetRaw = qaValue(lines, 'My preferred monthly budget ($)', stops);
    fields.budget = numericBudget(budgetRaw);
    const scope = qaValue(lines, 'What would you like RYNO to do with your existing LSA account?', stops);
    if (scope) fields.surveyNotes = `LSA scope: ${scope}`;
  }

  if (type === 'meta') {
    const stops = buildStopSet(META_QUESTIONS);
    fields.cid = qaValue(lines, 'What is your Meta Ad Account ID?', stops);
    const pageName = qaValue(lines, 'Facebook Page Name', stops);
    fields.facebookPageUrl = qaValue(lines, 'Facebook Page URL', stops);
    if (pageName) fields.surveyNotes = `Facebook Page Name: ${pageName}`;
    const pixelAnswer = qaValue(lines, 'Has the Meta Pixel (website tracking code) been installed on your current website?', stops);
    if (/not sure|unsure/i.test(pixelAnswer)) fields.metaPixelStatus = 'unsure';
    else if (/^yes/i.test(pixelAnswer)) fields.metaPixelStatus = 'installed';
    else if (/^no/i.test(pixelAnswer)) fields.metaPixelStatus = 'not_installed';
    const budgetRaw = qaValue(lines, 'Do you have a preferred total monthly budget for Meta Ads?', stops);
    fields.budget = numericBudget(budgetRaw);
  }

  return { fields, raw: text };
}
